import { formatPrice } from '../services/pricing/price-utils.js';

const FRAME_COLOR_MAP = {
  'Black': 'black',
  'Natural Wood': 'beige',
  'Beige': 'beige',
  'White': 'white',
  'No Frame': 'no-frame'
};

export function createGalleryVariantSelector() {
  return {
    selection_in_progress: false,
    available_sizes: [],
    available_frames: [],
    available_mounts: [],
    can_apply: false,

    get dimensionMetafields() {
      return window.galleryDimensionMetafields || null;
    },

    get selected_size() {
      return this.$store.galleryCore.variant_selection.size;
    },

    get selected_frame() {
      return this.$store.galleryCore.variant_selection.frame;
    },

    get selected_mount() {
      return this.$store.galleryCore.variant_selection.mount;
    },

    get total_price() {
      return this.$store.galleryCore.computed_total_price;
    },

    get price_display() {
      return this.$store.galleryCore.total_price_display;
    },

    get isSelectionComplete() {
      return Boolean(this.selected_size && this.selected_frame && this.selected_mount);
    },

    get applyButtonText() {
      if (this.selection_in_progress) {
        return 'Applying...';
      }

      if (!this.isSelectionComplete) {
        return 'Select all options';
      }

      if (this.can_apply) {
        return `Apply - ${this.price_display}`;
      }

      return 'Configuration unavailable';
    },

    get isApplyButtonDisabled() {
      return !this.can_apply || this.selection_in_progress;
    },


    getFrameSwatchClasses(frame, selectedFrame) {
      const classes = {
        'gallery-variant-frame__swatch--selected': selectedFrame === frame,
        'gallery-variant-frame__swatch--disabled': this.isFrameDisabled(frame)
      };

      const colorClass = FRAME_COLOR_MAP[frame];
      if (colorClass) {
        classes[`gallery-variant-frame__swatch--${colorClass}`] = true;
      }

      return classes;
    },

    getFrameCircleClasses(frame) {
      const colorClass = FRAME_COLOR_MAP[frame];
      if (!colorClass) return {};

      return {
        [`gallery-variant-frame__color-circle--${colorClass}`]: true
      };
    },

    isSizeSelected(size) {
      return this.selected_size === size;
    },

    isFrameSelected(frame) {
      return this.selected_frame === frame;
    },

    isMountSelected(mount) {
      return this.selected_mount === mount;
    },

    isSizeDisabled(size) {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) {
        store.initVariantManager();
      }

      return !store.variant_manager.isOptionSelectable(
        store.selected_artworks,
        'size',
        size,
        store.variant_selection
      );
    },

    isFrameDisabled(frame) {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) {
        store.initVariantManager();
      }

      const implicitSize = this.available_sizes?.[0] || null;

      return !store.variant_manager.isOptionSelectable(
        store.selected_artworks,
        'frame',
        frame,
        store.variant_selection,
        implicitSize
      );
    },

    isMountDisabled(mount) {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) {
        store.initVariantManager();
      }

      const implicitSize = this.available_sizes?.[0] || null;

      return !store.variant_manager.isOptionSelectable(
        store.selected_artworks,
        'mount',
        mount,
        store.variant_selection,
        implicitSize
      );
    },

    isOptionDisabled(type, option) {
      switch (type) {
        case 'size': return this.isSizeDisabled(option);
        case 'frame': return this.isFrameDisabled(option);
        case 'mount': return this.isMountDisabled(option);
        default: return false;
      }
    },

    getSizePriceDisplay(size) {
      const price = this.getSizePrice(size);
      return price ? formatPrice(price) : '';
    },

    getSizePrice(size) {
      const store = this.$store.galleryCore;
      if (!store.variant_manager) return 0;

      const isMember = typeof Alpine !== 'undefined' && Alpine.store('membership')?.isLoggedIn;
      const selected_artworks_count = store.selected_artworks?.length || 0;

      let totalPrice = 0;
      store.selected_artworks.forEach(artwork => {
        if (!artwork || !artwork.variants) return;

        const variantsWithSize = artwork.variants.filter(v =>
          v && v.option1 === size && v.available
        );

        if (variantsWithSize.length > 0) {
          const cheapestVariant = variantsWithSize.reduce((min, v) => {
            const vPrice = parseFloat(v.price || 0);
            const minPrice = parseFloat(min.price || 0);
            return vPrice < minPrice ? v : min;
          });

          let price;
          if (isMember && cheapestVariant.compare_at_price) {
            const memberPrice = parseFloat(cheapestVariant.compare_at_price);
            price = memberPrice > 0 ? memberPrice : parseFloat(cheapestVariant.price || 0);
          } else {
            price = parseFloat(cheapestVariant.price || 0);
          }
          totalPrice += price * 100;
        }
      });

      return totalPrice;
    },

    getFrameStyle(frame) {
      const frameColors = {
        'Black': '#000000',
        'White': '#FFFFFF',
        'Natural Wood': '#8B7355',
        'Gold': '#FFD700',
        'Silver': '#C0C0C0'
      };

      const color = frameColors[frame] || '#CCCCCC';

      if (frame === 'No Frame') {
        return `background: linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%, #f0f0f0),
                linear-gradient(45deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%, #f0f0f0);
                background-size: 10px 10px;
                background-position: 0 0, 5px 5px;`;
      }

      return `background-color: ${color}`;
    },

    getDimensions(size) {
      if (!this.dimensionMetafields) {
        return `${size} dimensions`;
      }

      const orientationRaw = this.$store.galleryCore?.orientation;
      if (!orientationRaw) {
        return `${size} dimensions`;
      }

      const orientation = orientationRaw.charAt(0).toUpperCase() + orientationRaw.slice(1);

      let mount = this.selected_mount || 'Mount';
      if (mount !== 'Full Bleed') {
        mount = 'Mount';
      }

      const dimensionData = this.dimensionMetafields?.[orientation]?.[mount]?.[size];

      if (dimensionData && dimensionData.Final) {
        return dimensionData.Final;
      }

      return `${size} dimensions`;
    },

    selectSize(size) {
      this.$store.galleryCore.setVariantOption('size', size);
      this.validateSelection();
      this.announceSelection('size', size);
    },

    selectFrame(frame) {
      this.$store.galleryCore.setVariantOption('frame', frame);
      this.validateSelection();
      this.announceSelection('frame', frame);

      if (this.selected_mount) {
        this.preloadCombination(frame, this.selected_mount, 'immediate');
      }

      this.scheduleAdjacentPreloads();
    },

    selectMount(mount) {
      this.$store.galleryCore.setVariantOption('mount', mount);
      this.validateSelection();
      this.announceSelection('mount', mount);

      if (this.selected_frame) {
        this.preloadCombination(this.selected_frame, mount, 'immediate');
      }

      this.scheduleAdjacentPreloads();
    },

    validateSelection() {
      const store = this.$store.galleryCore;
      const selection = store.variant_selection;

      const hasAllSelections = selection.size && selection.frame && selection.mount;

      if (!hasAllSelections) {
        this.can_apply = false;
        return;
      }

      const isValid = store.variant_manager.validateConfiguration(
        store.selected_artworks,
        selection.size,
        selection.frame,
        selection.mount
      );

      this.can_apply = isValid;

      if (!isValid) {
        alert('This combination is not available for all selected artworks');
      }
    },

    async applyConfiguration() {
      if (!this.can_apply || this.selection_in_progress) return;

      this.selection_in_progress = true;

      try {
        const store = this.$store.galleryCore;
        const config = {
          size: store.variant_selection.size,
          frame: store.variant_selection.frame,
          mount: store.variant_selection.mount
        };

        const success = await store.applyBulkVariantConfig(config);

        if (success) {
          this.closeModal();
          this.announceSuccess();
        } else {
          alert('Failed to apply configuration. Please try again.');
        }
      } catch (error) {
        alert('An error occurred. Please try again.');
      } finally {
        this.selection_in_progress = false;
      }
    },

    announceSelection(type, value) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `${type} selected: ${value}`;

      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 1000);
    },

    announceSuccess() {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Configuration applied to all artworks';

      document.body.appendChild(announcement);
      setTimeout(() => announcement.remove(), 2000);
    }
  };
}
